import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Box, Stack } from "@mui/material";
import { BrandColorPalette } from "../../../../utils/getColorMapping";
import ColorSwatch from "./ColorSwatch";

type InternBrandStylePaletteComparisonProps = {
  originalPalette?: BrandColorPalette;
  selectedPalette?: BrandColorPalette;
  size?: "small" | "large";
};

const InternBrandStylePaletteComparison = ({
  originalPalette,
  selectedPalette,
  size = "small",
}: InternBrandStylePaletteComparisonProps) => {
  const spacing = size === "large" ? 2 : 1;

  const renderArrow = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
      }}
    >
      <ArrowDownwardIcon fontSize="small" />
    </Box>
  );

  const renderSingleSwatch = (color: string | undefined, label: string) => (
    <Stack direction="row" spacing={spacing} justifyContent="center">
      <ColorSwatch color={color} label={label} size={size} />
    </Stack>
  );

  const renderComparisonSwatch = (
    originalColor: string | undefined,
    selectedColor: string | undefined,
    label: string
  ) => {
    const isDifferent = originalColor !== selectedColor;

    if (!isDifferent) {
      return renderSingleSwatch(originalColor, label);
    }

    return (
      <Stack direction="column" spacing={0.5} alignItems="center">
        <ColorSwatch color={originalColor} size={size} />
        {renderArrow()}
        <ColorSwatch color={selectedColor} label={label} size={size} />
      </Stack>
    );
  };

  const renderSwatchSet = (
    originalColor: string | undefined,
    selectedColor: string | undefined,
    label: string
  ) => {
    // Both unset - show empty swatches
    if (!originalColor && !selectedColor) {
      return renderSingleSwatch(undefined, label);
    }

    // Original set, selected not set or same
    if (originalColor && (!selectedColor || selectedColor === originalColor)) {
      return renderSingleSwatch(originalColor, label);
    }

    // Original not set, selected set
    if (!originalColor && selectedColor) {
      return (
        <Stack direction="column" spacing={0.5} alignItems="center">
          <ColorSwatch size={size} />
          {renderArrow()}
          <ColorSwatch color={selectedColor} label={label} size={size} />
        </Stack>
      );
    }

    // Both set - compare
    if (originalColor && selectedColor) {
      return renderComparisonSwatch(originalColor, selectedColor, label);
    }

    return null;
  };

  const renderAllSwatches = () => {
    const primary = renderSwatchSet(
      originalPalette?.primary,
      selectedPalette?.primary,
      "Primary"
    );
    const accent1 = renderSwatchSet(
      originalPalette?.accent1,
      selectedPalette?.accent1,
      "Accent 1"
    );
    const accent2 = renderSwatchSet(
      originalPalette?.accent2,
      selectedPalette?.accent2,
      "Accent 2"
    );
    const accent3 = renderSwatchSet(
      originalPalette?.accent3,
      selectedPalette?.accent3,
      "Accent 3"
    );

    return (
      <Stack direction="row" spacing={spacing} justifyContent="center">
        {primary}
        {accent1}
        {accent2}
        {accent3}
      </Stack>
    );
  };

  return <Box sx={{ p: 2 }}>{renderAllSwatches()}</Box>;
};

export default InternBrandStylePaletteComparison;
